import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  useGetAllMyPropertiesQuery,
  useLazyGetAllMyExperiencesQuery,
  useLazyGetAllMyHolidayPackagesQuery,
} from "application/features/host/hostApiSlice";
import Skeleton from "react-loading-skeleton";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import HeaderFilter from "containers/Home/HeaderFilter";
import { useTranslation } from "react-i18next";

interface TabNames {
  [key: string]: string;
}

type StatusFilter = "all" | "active" | "in_review" | "paused";

const isItemInReview = (item: any) => item.in_review || item.in_revision;
const isItemPaused = (item: any) => item.stopped;
const isItemActive = (item: any) => !isItemInReview(item) && !isItemPaused(item);

const filterByStatus = (items: any[], status: StatusFilter): any[] => {
  if (status === "all") return items;
  if (status === "active") return items.filter(isItemActive);
  if (status === "in_review") return items.filter(isItemInReview);
  if (status === "paused") return items.filter(isItemPaused);
  return items;
};

const PageMyListing: React.FC = () => {
  const { t, ready } = useTranslation("translation");
  const tabs: TabNames = t("translation:myListings.tabs", {
    returnObjects: true,
  });
  const tabsList: string[] = Object.values(tabs);

  const {
    data: properties,
    isLoading,
    isError,
  } = useGetAllMyPropertiesQuery({});
  const [
    getExperiences,
    { data: experiences, isLoading: isLoadingExperiences },
  ] = useLazyGetAllMyExperiencesQuery({});
  const [
    getHolidayPackages,
    { data: holidayPackages, isLoading: isLoadingHolidayPackages },
  ] = useLazyGetAllMyHolidayPackagesQuery({});

  const [activeTab, setActiveTab] = React.useState<string>(undefined);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");

  // Get the current tab's listing data
  const currentListings = useMemo(() => {
    if (activeTab === tabs.stays) return properties || [];
    if (activeTab === tabs.experiences) return experiences || [];
    if (activeTab === tabs.holidayPackages) return holidayPackages || [];
    return [];
  }, [activeTab, properties, experiences, holidayPackages, tabs]);

  // Compute counts per status for the current tab
  const statusCounts = useMemo(() => ({
    all: currentListings.length,
    active: currentListings.filter(isItemActive).length,
    in_review: currentListings.filter(isItemInReview).length,
    paused: currentListings.filter(isItemPaused).length,
  }), [currentListings]);

  const handleGetExperiences = async () => {
    try {
      await getExperiences({}).unwrap();
    } catch (err) {
      console.log(err);
    }
  };

  const handleGetHolidayPackages = async () => {
    try {
      await getHolidayPackages({}).unwrap();
    } catch (err) {
      console.log(err);
    }
  };

  const handleActiveTabChange = () => {
    if (activeTab === tabs.experiences) {
      handleGetExperiences();
    }
    if (activeTab === tabs.holidayPackages) {
      handleGetHolidayPackages();
    }
  };

  const renderCard = (item: any) => {
    if (activeTab === tabs.stays) {
      return (
        <MultiStockCard
          key={item.property_uuid}
          id={item.property_uuid}
          cover={item.cover}
          images={item.gallery_images}
          title={item.name}
          mood={item.mood}
          annotation={`${item.property_type === "apartment" ? "Appartmento" : item.property_type} · ${item.bedrooms} camere da letto`}
          address={item.address}
          price={Number(item.weekday_price)}
          rating={item.rating || item.score}
          reviewCount={item.reviews || item.reviews_count}
          href={"/host/edit-property/" + item.property_uuid}
          in_review={item.in_review || item.in_revision}
          stopped={item.stopped}
          showStatusBadge={true}
        >
          <button
            className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href =
                "/host/edit-property/" + item.property_uuid;
            }}
          >
            Modifica
          </button>
        </MultiStockCard>
      );
    }
    if (activeTab === tabs.experiences) {
      return (
        <MultiStockCard
          key={item.experience_uuid}
          id={item.experience_uuid}
          cover={item.cover}
          images={item.gallery_images}
          title={item.name}
          mood={item.mood}
          annotation={item.category}
          address={item.location}
          price={item.price}
          rating={item.rating || item.score}
          reviewCount={item.reviews || item.reviews_count}
          href={"/host/edit-experience/" + item.experience_uuid}
          in_review={item.in_review || item.in_revision}
          stopped={item.stopped}
          showStatusBadge={true}
        >
          <button
            className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href =
                "/host/edit-experience/" + item.experience_uuid;
            }}
          >
            Modifica
          </button>
        </MultiStockCard>
      );
    }
    if (activeTab === tabs.holidayPackages) {
      return (
        <MultiStockCard
          key={item.package_uuid}
          id={item.package_uuid}
          cover={item.cover}
          images={item.gallery_images}
          title={item.name}
          mood={item.mood}
          annotation={item.destination}
          address={item.location}
          description={item.description}
          price={item.price}
          rating={item.rating || item.score}
          reviewCount={item.reviews || item.reviews_count}
          href={"/host/edit-holiday-package/" + item.package_uuid}
          in_review={item.in_review || item.in_revision}
          stopped={item.stopped}
          showStatusBadge={true}
        >
          <button
            className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href =
                "/host/edit-holiday-package/" + item.package_uuid;
            }}
          >
            Modifica
          </button>
        </MultiStockCard>
      );
    }
  };

  const handleChangeTab = () => {
    if (activeTab === tabs.stays) {
      if (properties) {
        const filtered = filterByStatus(properties, statusFilter);
        if (filtered.length === 0) return renderEmptyFilterState();
        return filtered.map((item: any) => renderCard(item));
      }
    }
    if (activeTab === tabs.experiences) {
      if (experiences) {
        const filtered = filterByStatus(experiences, statusFilter);
        if (filtered.length === 0) return renderEmptyFilterState();
        return filtered.map((item: any) => renderCard(item));
      }
    }
    if (activeTab === tabs.holidayPackages) {
      if (holidayPackages) {
        const filtered = filterByStatus(holidayPackages, statusFilter);
        if (filtered.length === 0) return renderEmptyFilterState();
        return filtered.map((item: any) => renderCard(item));
      }
    }
    return (
      <div className="col-span-full flex flex-col items-center justify-center py-20">
        <h3 className="text-2xl font-semibold mb-4">
          {t("translation:myListings.inactiveContent.title")}
        </h3>
        <p className="text-neutral-500">
          {t("translation:myListings.inactiveContent.description")}
        </p>
      </div>
    );
  };

  const renderEmptyFilterState = () => (
    <div className="col-span-full flex flex-col items-center justify-center py-20">
      <p className="text-neutral-500">
        {t("translation:myListings.inactiveContent.title")}
      </p>
    </div>
  );

  useEffect(() => {
    if (tabsList && tabsList.length > 0) {
      setActiveTab(tabsList[0]);
    }
  }, []);

  useEffect(() => {
    handleActiveTabChange();
  }, [activeTab]);

  if (!ready) return null;

  return (
    <div className="nc-annunci">
      <Helmet>
        <title>{t("translation:myListings.title")} | TRIBYOU</title>
      </Helmet>
      <div className="container relative pt-5 pb-16 lg:pb-20 lg:pt-8 flex flex-col">
        <HeaderFilter
          tabActive={activeTab}
          tabs={tabsList}
          heading={t("translation:myListings.title")}
          subHeading={t("translation:myListings.description")}
          onClickTab={(e) => {
            setActiveTab(e);
            setStatusFilter("all");
          }}
        />
        <div className="flex flex-wrap gap-2 mb-6">
          {([
            { key: "all" as StatusFilter, label: t("translation:myListings.filters.all"), color: "bg-neutral-200 text-neutral-700", activeColor: "bg-neutral-800 text-white" },
            { key: "active" as StatusFilter, label: t("translation:myListings.filters.active"), color: "bg-emerald-100 text-emerald-700", activeColor: "bg-emerald-500 text-white" },
            { key: "in_review" as StatusFilter, label: t("translation:myListings.filters.inReview"), color: "bg-amber-100 text-amber-700", activeColor: "bg-amber-500 text-white" },
            { key: "paused" as StatusFilter, label: t("translation:myListings.filters.paused"), color: "bg-red-100 text-red-700", activeColor: "bg-red-500 text-white" },
          ]).map(({ key, label, color, activeColor }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                statusFilter === key ? activeColor : color
              }`}
            >
              {label} ({statusCounts[key]})
            </button>
          ))}
        </div>
        <div
          className={`grid gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}
        >
          {isLoading || isLoadingExperiences ? (
            <>
              <Skeleton className="w-full h-[10rem] md:h-[10rem]" />
              <Skeleton className="w-full h-[10rem] md:h-[10rem]" />
              <Skeleton className="w-full h-[10rem] md:h-[10rem]" />
            </>
          ) : (
            handleChangeTab()
          )}
        </div>
      </div>
    </div>
  );
};

export default PageMyListing;
